import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Trash2, Edit, Eye, Replace, Upload, ExternalLink, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://backend.dlapakistan.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

const MembersAreaCards = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('cards');
  
  // Card states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  
  // Link states
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isLinkViewModalOpen, setIsLinkViewModalOpen] = useState(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [currentLink, setCurrentLink] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [links, setLinks] = useState([]);
  
  // Card form data
  const [formData, setFormData] = useState({
    title: '',
    buttonText: '',
    buttonLink: '',
    image: null,
  });
  
  // Link form data
  const [linkFormData, setLinkFormData] = useState({
    title: '',
    description: '',
    linkUrl: '',
    buttonText: '',
  });

  const fileInputRef = useRef(null);

  // Fetch cards from backend
  useEffect(() => {
    fetchCards();
    fetchLinks();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await api.get('/members-area/cards/');
      let cardsData = Array.isArray(response.data) ? response.data : 
                        (response.data.results ? response.data.results : 
                        (response.data.data ? response.data.data : []));
      
      // Sort cards by created_at in ascending order (oldest first)
      cardsData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      setCards(cardsData);
    } catch (err) {
      console.error('Fetch error:', err);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const response = await api.get('/members-area/links/');
      let linksData = Array.isArray(response.data) ? response.data : 
                        (response.data.results ? response.data.results : 
                        (response.data.data ? response.data.data : []));
      
      // Sort links by created_at in ascending order (oldest first)
      linksData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      setLinks(linksData);
    } catch (err) {
      console.error('Fetch links error:', err);
      setLinks([]);
    } finally {
      setLoadingLinks(false);
    }
  };

  // Handle image change for cards
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setFormData(prev => ({ ...prev, image: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit card function
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Please enter a title');
      return;
    }
    if (!formData.buttonText.trim()) {
      alert('Please enter button text');
      return;
    }
    if (!formData.buttonLink.trim()) {
      alert('Please enter button link URL');
      return;
    }
    
    const data = new FormData();
    data.append('title', formData.title);
    data.append('button_text', formData.buttonText);
    data.append('button_link', formData.buttonLink);
    
    if (formData.image) {
      data.append('image', formData.image);
    }
    
    try {
      if (isEditMode && currentCard) {
        await api.patch(`/members-area/cards/${currentCard.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        alert('Card updated successfully!');
      } else {
        await api.post('/members-area/cards/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        alert('Card added successfully!');
      }
      
      await fetchCards();
      handleCloseModal();
    } catch (err) {
      console.error('Error:', err.response?.data || err.message);
      alert(err.response?.data?.message || 'Failed to save card');
    }
  };

  // Submit link function
  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    
    if (!linkFormData.title.trim()) {
      alert('Please enter a title');
      return;
    }
    if (!linkFormData.linkUrl.trim()) {
      alert('Please enter link URL');
      return;
    }
    
    let validUrl = linkFormData.linkUrl.trim();
    if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
      validUrl = 'https://' + validUrl;
    }
    
    try {
      if (isLinkEditMode && currentLink) {
        await api.patch(`/members-area/links/${currentLink.id}/`, {
          title: linkFormData.title,
          description: linkFormData.description,
          link_url: validUrl,
          button_text: linkFormData.buttonText || 'Visit Website',
        });
        alert('Link updated successfully!');
      } else {
        await api.post('/members-area/links/', {
          title: linkFormData.title,
          description: linkFormData.description,
          link_url: validUrl,
          button_text: linkFormData.buttonText || 'Visit Website',
          is_active: true,
        });
        alert('Link added successfully!');
      }
      
      await fetchLinks();
      handleCloseLinkModal();
    } catch (err) {
      console.error('Error:', err.response?.data || err.message);
      alert(err.response?.data?.message || 'Failed to save link');
    }
  };

  // Delete card
  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      try {
        await api.delete(`/members-area/cards/${cardId}/`);
        await fetchCards();
        alert('Card deleted successfully!');
      } catch (err) {
        console.error('Delete error:', err);
        alert('Failed to delete card');
      }
    }
  };

  // Delete link
  const handleDeleteLink = async (linkId) => {
    if (window.confirm('Are you sure you want to delete this link? This action cannot be undone.')) {
      try {
        await api.delete(`/members-area/links/${linkId}/`);
        await fetchLinks();
        alert('Link deleted successfully!');
      } catch (err) {
        console.error('Delete error:', err);
        alert('Failed to delete link');
      }
    }
  };

  // Edit card
  const handleEditCard = (card) => {
    setCurrentCard(card);
    setFormData({
      title: card.title,
      buttonText: card.button_text,
      buttonLink: card.button_link,
      image: null,
    });
    setImagePreview(card.image_url);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Edit link
  const handleEditLink = (link) => {
    setCurrentLink(link);
    setLinkFormData({
      title: link.title,
      description: link.description || '',
      linkUrl: link.link_url,
      buttonText: link.button_text || 'Visit Website',
    });
    setIsLinkEditMode(true);
    setIsLinkModalOpen(true);
  };

  // View card
  const handleViewCard = (card) => {
    setCurrentCard(card);
    setIsViewModalOpen(true);
  };

  // View link
  const handleViewLink = (link) => {
    setCurrentLink(link);
    setIsLinkViewModalOpen(true);
  };

  // Handle button click
  const handleButtonClick = (buttonLink, e) => {
    if (e) e.stopPropagation();
    if (buttonLink) {
      window.open(buttonLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Close card modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentCard(null);
    setImagePreview(null);
    setFormData({ title: '', buttonText: '', buttonLink: '', image: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Close link modal
  const handleCloseLinkModal = () => {
    setIsLinkModalOpen(false);
    setIsLinkEditMode(false);
    setCurrentLink(null);
    setLinkFormData({ title: '', description: '', linkUrl: '', buttonText: '' });
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setCurrentCard(null);
  };

  const handleCloseLinkViewModal = () => {
    setIsLinkViewModalOpen(false);
    setCurrentLink(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getImageUrl = (card) => {
    return card.image_url || 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render cards grid (oldest first)
  const renderCardsGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
      {cards.map((card) => (
        <div key={card.id} className="bg-white rounded-lg border border-gray-200 hover:border-emerald-300 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md group flex flex-col">
          <div className="relative h-64 overflow-hidden">
            <img 
              src={getImageUrl(card)}
              alt={card.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
              }}
            />
            <div className="absolute top-2 right-2 flex space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCard(card);
                }}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-emerald-50 shadow-sm transition-all duration-200 hover:scale-105"
                title="Edit"
              >
                <Edit className="w-4 h-4 text-emerald-600" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCard(card.id);
                }}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-red-50 shadow-sm transition-all duration-200 hover:scale-105"
                title="Delete"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            <div onClick={() => handleViewCard(card)} className="cursor-pointer flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                {card.title}
              </h3>
            </div>
            
            <button
              onClick={(e) => handleButtonClick(card.button_link, e)}
              className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium text-sm hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg group/btn"
            >
              <span>{card.button_text || 'Learn More'}</span>
              <ExternalLink className="w-4 h-4 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
            </button>
            
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
              <span className="text-xs text-emerald-600 font-medium">
                {formatDate(card.created_at)}
              </span>
              <Eye 
                className="w-4 h-4 text-emerald-500 cursor-pointer hover:text-emerald-600 transition-colors" 
                onClick={() => handleViewCard(card)}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render links as simple rows (oldest first)
  const renderLinksList = () => (
    <div className="space-y-4 mt-8">
      {links.map((link) => (
        <div key={link.id} className="bg-white rounded-lg border border-gray-200 hover:border-emerald-300 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md">
          <div className="p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              {/* Left side content */}
              <div className="flex-1 cursor-pointer" onClick={() => handleViewLink(link)}>
                <h3 className="text-lg font-bold text-emerald-600 mb-2">
                  {link.title}
                </h3>
                {link.description && (
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {link.description}
                  </p>
                )}
              </div>
              
              {/* Right side - button and actions */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={(e) => handleButtonClick(link.link_url, e)}
                  className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium text-sm hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  <span>{link.button_text || 'Visit Website'}</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditLink(link)}
                    className="p-2 hover:bg-emerald-50 rounded-lg transition-all"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4 text-emerald-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteLink(link.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                  <button
                    onClick={() => handleViewLink(link)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-3 pt-2 border-t border-gray-100">
              <span className="text-xs text-emerald-600 font-medium">
                Added: {formatDate(link.created_at)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const animationStyles = `
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fade-in-up {
      animation: slideUp 0.3s ease-out;
    }
  `;

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <style>{animationStyles}</style>
      
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-8 py-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Members Area Content</h1>
            <p className="text-gray-600 mt-2">Manage cards and external links for members area</p>
          </div>
          
          {activeTab === 'cards' ? (
            <button
              onClick={() => {
                setIsEditMode(false);
                setImagePreview(null);
                setFormData({ title: '', buttonText: '', buttonLink: '', image: null });
                setIsModalOpen(true);
              }}
              className="flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Card
            </button>
          ) : (
            <button
              onClick={() => {
                setIsLinkEditMode(false);
                setLinkFormData({ title: '', description: '', linkUrl: '', buttonText: '' });
                setIsLinkModalOpen(true);
              }}
              className="flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Link
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-8 pt-6 border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('cards')}
            className={`pb-3 px-2 font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'cards'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Cards
            <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
              {cards.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('links')}
            className={`pb-3 px-2 font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'links'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LinkIcon className="w-4 h-4" />
            External Links
            <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
              {links.length}
            </span>
          </button>
        </div>
      </div>

      <div className="p-8 pt-6">
        {activeTab === 'cards' ? (
          loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading cards...</p>
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
                <Plus className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No cards yet</h3>
              <p className="text-gray-500 mb-6">Add your first member card to get started</p>
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setImagePreview(null);
                  setFormData({ title: '', buttonText: '', buttonLink: '', image: null });
                  setIsModalOpen(true);
                }}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Add Card
              </button>
            </div>
          ) : (
            renderCardsGrid()
          )
        ) : (
          loadingLinks ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading links...</p>
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
                <Plus className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No links yet</h3>
              <p className="text-gray-500 mb-6">Add your first external link to get started</p>
              <button
                onClick={() => {
                  setIsLinkEditMode(false);
                  setLinkFormData({ title: '', description: '', linkUrl: '', buttonText: '' });
                  setIsLinkModalOpen(true);
                }}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Add Link
              </button>
            </div>
          ) : (
            renderLinksList()
          )
        )}
      </div>

      {/* Add/Edit Card Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-fade">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditMode ? 'Edit' : 'Add'} Member Card
              </h2>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-5">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Card Image *</label>
                  {imagePreview ? (
                    <div className="border-2 border-dashed border-emerald-200 rounded-xl p-4 bg-emerald-50/30">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-contain rounded-lg mb-4" />
                      <div className="flex justify-center space-x-3">
                        <button type="button" onClick={triggerFileInput} className="flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                          <Replace className="w-4 h-4 mr-2" />
                          Replace
                        </button>
                        <button type="button" onClick={handleRemoveImage} className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div onClick={triggerFileInput} className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 bg-gray-50 hover:bg-gray-100">
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to upload image</p>
                      <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    required
                    placeholder="Enter card title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Text *</label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData(prev => ({ ...prev, buttonText: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    required
                    placeholder="e.g., Learn More, Shop Now"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Link URL *</label>
                  <input
                    type="url"
                    value={formData.buttonLink}
                    onChange={(e) => setFormData(prev => ({ ...prev, buttonLink: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    required
                    placeholder="https://example.com/page"
                  />
                  <p className="text-xs text-gray-500 mt-1">Will open in a new tab when clicked</p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
                <button type="button" onClick={handleCloseModal} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl hover:from-emerald-600 hover:to-green-600 font-medium shadow-sm">
                  {isEditMode ? 'Update Card' : 'Add Card'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Link Modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-fade">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">
                {isLinkEditMode ? 'Edit' : 'Add'} External Link
              </h2>
              <button onClick={handleCloseLinkModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleLinkSubmit}>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={linkFormData.title}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    required
                    placeholder="e.g., ICO (Information Commissioner's Office)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={linkFormData.description}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none h-28 resize-none"
                    placeholder="Enter description for this link"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Link URL *</label>
                  <input
                    type="url"
                    value={linkFormData.linkUrl}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    required
                    placeholder="https://example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Will open in a new tab when clicked</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                  <input
                    type="text"
                    value={linkFormData.buttonText}
                    onChange={(e) => setLinkFormData(prev => ({ ...prev, buttonText: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
                    placeholder="Visit Website (default)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to use "Visit Website"</p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
                <button type="button" onClick={handleCloseLinkModal} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl hover:from-emerald-600 hover:to-green-600 font-medium shadow-sm">
                  {isLinkEditMode ? 'Update Link' : 'Add Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Card Modal */}
      {isViewModalOpen && currentCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-fade">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Card Details</h2>
              <button onClick={handleCloseViewModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <img src={getImageUrl(currentCard)} alt={currentCard.title} className="w-full h-64 object-cover rounded-lg mb-4 shadow-sm" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{currentCard.title}</h3>
              
              <button
                onClick={() => handleButtonClick(currentCard.button_link, { stopPropagation: () => {} })}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 mb-4"
              >
                <span>{currentCard.button_text || 'Learn More'}</span>
                <ExternalLink className="w-4 h-4" />
              </button>
              
              <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-600">Created</span>
                  <span>{formatDate(currentCard.created_at)}</span>
                </div>
                <div className="flex flex-col text-right">
                  <span className="font-medium text-gray-600">Last updated</span>
                  <span>{formatDate(currentCard.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Link Modal */}
      {isLinkViewModalOpen && currentLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-fade">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Link Details</h2>
              <button onClick={handleCloseLinkViewModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="p-3 rounded-lg bg-emerald-50 inline-block mb-4">
                <LinkIcon className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold text-emerald-600 mb-3">{currentLink.title}</h3>
              {currentLink.description && (
                <p className="text-gray-700 leading-relaxed mb-4">{currentLink.description}</p>
              )}
              
              <button
                onClick={() => handleButtonClick(currentLink.link_url, { stopPropagation: () => {} })}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 mb-4"
              >
                <span>{currentLink.button_text || 'Visit Website'}</span>
                <ExternalLink className="w-4 h-4" />
              </button>
              
              <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-600">Created</span>
                  <span>{formatDate(currentLink.created_at)}</span>
                </div>
                <div className="flex flex-col text-right">
                  <span className="font-medium text-gray-600">Last updated</span>
                  <span>{formatDate(currentLink.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            backdrop-filter: blur(0);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(2px);
          }
        }
        .modal-fade {
          animation: fadeIn 0.2s ease-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default MembersAreaCards;